import SDiagBase from "./SDiagBase";

const ARCHETYPES = [
  {
    num: "01", color: "seek", name: "The Seeker",
    tagline: "The desire to leave is stronger than the idea of what comes next.",
    driver: "Escape from stagnation and dissatisfaction",
    body: "The Seeker feels emotionally restless and misaligned in their current role. The pivot is driven more by the need for change than by a clearly defined business idea. Relief feels urgent.",
    strengths: ["Courage to question comfort", "High sensitivity to misalignment", "Openness to reinvention", "Emotional honesty"],
    blindspots: ["Impulsive resignation", "Moving away without moving toward", "Financial instability from urgency", "Fatigue mistaken for calling"],
    approach: "Slow down and build clarity before you exit. Test small ideas while still employed. Your first step is not to resign — it is to create stability.",
  },
  {
    num: "02", color: "evol", name: "The Evolver",
    tagline: "The desire for meaningful work and personal growth is leading the move.",
    driver: "Purpose and internal alignment",
    body: "The Evolver seeks growth and deeper alignment. The pivot is driven by a desire for meaningful work and personal expansion rather than dissatisfaction alone. Patience and intentionality come naturally.",
    strengths: ["Long-term thinking", "Strong intrinsic motivation", "Patience and intentionality", "High self-awareness"],
    blindspots: ["Over-refinement before launch", "Waiting for perfect alignment", "Idealising impact without testing demand", "Endless preparation"],
    approach: "Design thoughtfully, but validate early. Purpose is the strength — but it must be tested with the market. Clarity comes through action, not just reflection.",
  },
  {
    num: "03", color: "aspi", name: "The Aspirer",
    tagline: "The ambition to build something large, impactful, and financially rewarding is driving the move.",
    driver: "Growth, leverage, and long-term upside",
    body: "The Aspirer is driven by ambition, scale, and wealth creation. The desire to pivot is fuelled by the aspiration to build something larger and more rewarding. Confidence in independence comes naturally.",
    strengths: ["High ambition and enterprise thinking", "Confidence in independence", "Comfort with risk", "Long-term vision"],
    blindspots: ["Premature scaling", "Overestimating early traction", "Ego-driven expansion", "Capital overexposure"],
    approach: "Start controlled, scale later. Validate unit economics before expanding. Scale is earned, not assumed — discipline must lead ambition.",
  },
];

export default function S0DiagnosticPage() {
  // v11: 3-signal architecture — Navigator removed.
  const radarScores = { Seeker: 2.4, Evolver: 3.8, Aspirer: 2.8 };
  return (
    <SDiagBase
      stage="S0"
      isFree
      pageUrl="https://thepivotbox.com/S0diagnostic"
      pageTitle="Pivotbox S0 — Discover Your Pivot"
      testId="s0-diagnostic-page"
      ctaTo="/foundersignaltest"
      hero={{
        eyebrowTrail: "Discover Your Pivot · S0 · Free",
        headlineLead: "Before you plan the business,",
        headlineItalic: "understand the founder.",
        sub: "Most professionals spend months evaluating the idea. S0 asks the question that comes before the idea: what is actually driving you to make this move — and what does that mean for how you should build?",
        ctaLabel: "Founder Signal Test",
        priceLabel: <>Always free &middot; <strong>&nbsp;15 questions</strong></>,
      }}
      intro={{
        label: "Who S0 is built for",
        body: "You have a corporate career behind you. The income is good. But there is a growing sense that the work no longer fits — or a business idea that will not go away. Before any structural evaluation begins, S0 identifies your psychological starting position.",
        bullets: [
          { strong: "Running away or running toward?", body: "Most professionals who consider a transition are driven by escape. Successful founders are driven by something they are building toward. S0 identifies which applies to you." },
          { strong: "Your archetype shapes everything that follows.", body: "Pacing, risk tolerance, business model fit, and what will keep you going through year one — all of these are downstream of your pivot profile." },
          { strong: "It is the first step, not the only step.", body: "S0 is directional. The deeper structural assessment — your financial readiness, support environment, durability, and capability — begins with S1." },
        ],
        methodologyCols: [
          { label: "What it measures", body: "S0 identifies your <strong>dominant transition impulse</strong> — the psychological driver behind your desire to pivot. It is not a personality test. It is a directional signal about what kind of founder you are likely to be and what your first year requires." },
          { label: "Why three archetypes", body: "Professionals move for three core reasons: <strong>escape, growth, or ambition.</strong> These are psychologically distinct and behaviourally predictive. Fewer collapse important nuance. More create personality profiling rather than clarity." },
          { label: "What it is not", body: "S0 does not evaluate structural readiness, capability, or timing. It <strong>precedes</strong> that evaluation. Without knowing your psychological starting point, the structural assessment that follows lacks essential context." },
        ],
      }}
      why={{
        eyebrow: "Why this comes first",
        headlineLead: "Most corporate professionals leave running away.",
        headlineItalic: "Successful founders leave running toward.",
        paragraphs: [
          "The most common reason a corporate professional considers entrepreneurship is dissatisfaction — a bad manager, a glass ceiling, burnout, or the slow realisation that the work stopped meaning anything. These are real and valid. But they are not a business strategy.",
          "Founders who build businesses out of escape tend to recreate the same pressures in a different context. The urgency that drives them to leave also drives them to make impulsive decisions, under-prepare financially, and move toward the first idea that provides relief rather than the idea that fits.",
          "S0 strips away the noise to identify <strong>what is actually driving you</strong> — and what that means for how you should pace the transition, what business models will make you happy long-term, and what your specific blind spots are before you start building.",
        ],
        callout: "Without knowing your \u201Cwhy,\u201D you will likely recreate the same stress of your corporate job in your new business. S0 prevents the Founder's Regret — two years building something you end up hating as much as your old role.",
      }}
      measures={{
        eyebrow: "The four pivot profiles",
        headline: "Four archetypes. Each with a different starting point — and a different path forward.",
        intro: "S0 identifies your dominant profile across four psychologically distinct transition impulses. Most people have elements of all four — S0 identifies which one is driving your decisions right now.",
        cards: (
          <div className="sd-arch-grid" data-testid="s0-archetype-grid">
            {ARCHETYPES.map((a) => (
              <div className="sd-arch-card" key={a.num} data-color={a.color} data-testid={`s0-arch-${a.num}`}>
                <span className="sd-arch-num">{a.num}</span>
                <h3 className="sd-arch-name">{a.name}</h3>
                <p className="sd-arch-tagline">{a.tagline}</p>
                <p className="sd-arch-driver"><strong>Core driver:</strong> {a.driver}</p>
                <p className="sd-arch-body">{a.body}</p>
                <div className="sd-arch-lists">
                  <div>
                    <div className="sd-arch-list-label">Strengths</div>
                    <ul className="sd-arch-list">{a.strengths.map((s) => <li key={s}>{s}</li>)}</ul>
                  </div>
                  <div>
                    <div className="sd-arch-list-label">Blind spots</div>
                    <ul className="sd-arch-list">{a.blindspots.map((s) => <li key={s}>{s}</li>)}</ul>
                  </div>
                </div>
                <p className="sd-arch-approach"><strong>Recommended approach:</strong> {a.approach}</p>
              </div>
            ))}
          </div>
        ),
      }}
      receive={{
        eyebrow: "What your result shows",
        eyebrowAccent: "teal",
        callout: "S0 is the only assessment in the Pivotbox suite that is <strong>always free</strong>. No account, no payment, no follow-up sales call.",
        heading: "Your dominant pivot profile — and exactly what it means for how you should move.",
        intro: "S0 produces an instant profile result showing your dominant and secondary archetype scores, your core driver, your structural blind spots, and a specific recommended approach for your profile.",
        bullets: [
          { strong: "Dominant archetype", body: "— your primary pivot profile with a full narrative on what it means for your transition." },
          { strong: "Scores across all four profiles", body: "— so you understand your full psychological landscape, not just a single label." },
          { strong: "Blind spots and recommended approach", body: "— specific to your profile, not generic entrepreneurship advice." },
          { strong: "Context for S1", body: "— your pivot profile informs how the structural readiness assessment that follows should be interpreted." },
        ],
        report: (
          <div className="s1d-report sd-report-archetype" data-testid="s0-report-sample">
            <div className="s1d-report__head">
              <span className="s1d-report__title">Pivotbox — S0 Result</span>
              <span className="s1d-report__badge">FREE</span>
            </div>
            <div className="s1d-report__band">
              <span className="s1d-report__band-label">SCORE PROFILE - ALL SIGNALS</span>
              <span className="s1d-report__band-value">Out of 25</span>
            </div>
            <div style={{ padding: "1.4rem 1rem 1.6rem", display: "flex", justifyContent: "center" }}>
              <S0SampleRadar scores={radarScores} />
            </div>
          </div>
        ),
      }}
      helps={{
        eyebrow: "How S0 helps",
        heading: "Three things S0 makes clear.",
        intro: "S0 is directional, not structural. It does not tell you whether you are ready to transition. It tells you what is driving you — and what that means for how you should approach everything that follows.",
        cards: [
          { num: "01", title: "Why you want to make this move — not just that you do", body: "S0 distinguishes between escape, growth, ambition, and security as primary drivers. Each has a different implication for what kind of business will make you happy long-term — not just profitable short-term." },
          { num: "02", title: "What your specific blind spots are before you start", body: "Every pivot profile has a predictable failure mode. Seekers move too fast. Evolvers over-prepare. Aspirers scale prematurely. Knowing yours is the first step to managing it." },
          { num: "03", title: "How to pace your transition for your profile", body: "The right speed, risk tolerance, and first move depends entirely on your psychological starting point. S0 gives you that context — so the structural evaluation in S1 and S2 is interpreted with the right lens." },
        ],
      }}
      framework={[
        { stage: "S0",   name: "Discover Your Pivot",                     q: "What is driving you to make this move — and what does that mean for how you should build?", badge: "You are here", isCurrent: true, isFree: true },
        { stage: "S1",   name: "Founder Reality Check",                   q: "Is your life right now positioned to support this transition?" },
        { stage: "S2",   name: "Founder Diagnostic",                      q: "Who are you as a founder — how you build, hold up, and execute." },
        { stage: "S2.1", name: "Founder Archetype Assessment",            q: "How are you naturally wired to build?", isSub: true, isFuture: true },
        { stage: "S2.2", name: "Founder Durability Assessment",           q: "How do you respond to failure, setback, and sustained uncertainty?", isSub: true, isFuture: true },
        { stage: "S2.3", name: "Founder Capability & Execution Profile",  q: "Do you have the capabilities to execute your intended business model?", isSub: true, isFuture: true },
        { stage: "S3",   name: "Strategy Blueprint",                      q: "Is the venture concept, model, and market validated enough to commit?" },
        { stage: "S4",   name: "Founder Operating System",                q: "How do you go from first revenue to a business that scales?" },
      ]}
      finalCta={{
        heading: "Start here. Before the plan, before the idea — before anything structural.",
        sub: "Fifteen questions. An instant pivot profile. A clear picture of what is actually driving you and what it means for how you should build. No cost, no account required.",
        price: { strike: 0, current: 0 },
        ctaLabel: "Founder Signal Test",
      }}
    />
  );
}

/* Radar chart for the S0 sample report card — 3-axis triangular, mirrors S0ReportPage.jsx */
function S0SampleRadar({ scores }) {
  const cx = 170;
  const cy = 140;
  const R = 88;
  const VBW = 340;
  const VBH = 280;
  const axes = [
    { type: "Seeker",  angle: 270, col: "#8B2A1A" },  // top
    { type: "Aspirer", angle: 30,  col: "#5B2D8E" },  // bottom-right
    { type: "Evolver", angle: 150, col: "#1B6B5A" },  // bottom-left
  ];
  const toRad = (deg) => (deg * Math.PI) / 180;
  const pts = axes.map((a) => {
    const s = Number(scores[a.type] || 0);
    // v11: /5 decimal scale
    const r = (s / 5) * R;
    return {
      ...a,
      score: s,
      x: cx + r * Math.cos(toRad(a.angle)),
      y: cy + r * Math.sin(toRad(a.angle)),
      lx: cx + (R + 32) * Math.cos(toRad(a.angle)),
      ly: cy + (R + 32) * Math.sin(toRad(a.angle)),
    };
  });
  const polygon = pts.map((p) => `${p.x},${p.y}`).join(" ");
  const grid = [0.25, 0.5, 0.75, 1.0].map((f) => (
    <circle key={f} cx={cx} cy={cy} r={R * f} fill="none" stroke="#E4DDD2" strokeWidth="0.7" />
  ));
  // Spokes to each vertex
  const spokes = axes.map((a) => {
    const rad = toRad(a.angle);
    const ex = cx + R * Math.cos(rad);
    const ey = cy + R * Math.sin(rad);
    return <line key={a.type} x1={cx} y1={cy} x2={ex} y2={ey} stroke="#E4DDD2" strokeWidth="0.7" />;
  });
  return (
    <svg viewBox={`0 0 ${VBW} ${VBH}`} width="100%" style={{ maxWidth: 380 }} data-testid="s0-sample-radar" aria-label="Sample S0 radar chart">
      {grid}
      {spokes}
      <polygon points={polygon} fill="#5B2D8E" fillOpacity="0.1" stroke="#5B2D8E" strokeWidth="2" />
      {pts.map((p) => (
        <g key={p.type}>
          <circle cx={p.x} cy={p.y} r="5" fill={p.col} stroke="white" strokeWidth="1.5" />
          <text x={p.lx} y={p.ly - 4} textAnchor="middle" fill={p.col} fontSize="11" fontWeight="700">{p.type}</text>
          <text x={p.lx} y={p.ly + 10} textAnchor="middle" fill="#7A7268" fontSize="9">{p.score.toFixed(1)}/5</text>
        </g>
      ))}
    </svg>
  );
}
